import React, { FC } from 'react';
import { FormikValues, useFormikContext } from 'formik';
import { InputField } from '@components/Form/InputField';
import { WorkingHours } from '@components/WorkingHours';
import { Schedule } from '@constants/daysSchedule';
import { TLocations } from '@pages/Locations/LocationsTable/types';
import { IDictionary } from '@constants/languages/types';
import { PhoneField } from '@components/Form/PhoneField';
import { useModal } from '@components/Modal/context/useModalContext';
import { WarningMessage } from '@components/WarningMessage';
import { ButtonVariants } from '@components/Button/types';
import { InfoVariants } from '@components/Info/types';
import { Info } from '@components/Info';

interface IFormContentProps {
  dictionary: IDictionary;
  onSubmitFormForce?: (formValues: FormikValues) => void;
}

const defaultSchedule: Schedule = {
  days: {
    sunday: [],
    monday: [
      {
        from: '',
        to: '',
      },
    ],
    tuesday: [
      {
        from: '',
        to: '',
      },
    ],
    wednesday: [
      {
        from: '',
        to: '',
      },
    ],
    thursday: [
      {
        from: '',
        to: '',
      },
    ],
    friday: [
      {
        from: '',
        to: '',
      },
    ],
    saturday: [],
  },
};

export const editLocationInitialValues = (
  name: string,
  number: string,
  address: string,
  llph: number,
  id?: number,
  schedule?: Schedule,
) => ({
  name,
  number,
  address,
  llph,
  id,
  schedule: schedule ?? defaultSchedule,
});

export const addLocationInitialValues: TLocations = {
  name: '',
  number: '',
  address: '',
  llph: 1,
  schedule: defaultSchedule,
};

export const FormContent: FC<IFormContentProps> = ({ dictionary, onSubmitFormForce }) => {
  const { modalError } = useModal();

  const { values: formValues, errors }: FormikValues = useFormikContext();

  return (
    <div className="form-field">
      <div className="row">
        <div className="col-xs-12">
          <InputField
            type="text"
            placeholder={dictionary.placeholders.locationName}
            label={dictionary.labels.locationName}
            required
            name="name"
          />
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12">
          <PhoneField
            type="text"
            placeholder={dictionary.placeholders.enterPhoneNumber}
            label={dictionary.labels.phoneNumber}
            required
            name="number"
          />
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12">
          <InputField
            type="text"
            placeholder={dictionary.placeholders.locationAddress}
            label={dictionary.labels.locationAddress}
            required
            name="address"
          />
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12">
          <InputField
            type="number"
            placeholder={dictionary.placeholders.enterPlph}
            label={dictionary.labels.llph}
            required
            name="llph"
            min={1}
          />
        </div>
      </div>
      <div className="working-hours">
        {!!Object.keys(errors).length && (
          <Info text={dictionary.physician.modals.editPhysician.errorTitle} variant="warning" />
        )}
        <ul className="working-hours-list">
          {Object.keys(addLocationInitialValues?.schedule?.days ?? defaultSchedule).map(
            (day, index) => (
              <WorkingHours day={day} dictionary={dictionary} key={index} />
            ),
          )}
        </ul>
      </div>
      {!!modalError && (
        <WarningMessage
          title={dictionary.blackout.modals.errorMessage.title}
          buttonVariants={ButtonVariants.danger}
          buttonLabel="Change limit"
          variant={InfoVariants.warning}
          onButtonClick={() => onSubmitFormForce && onSubmitFormForce(formValues)}
          message={`${modalError}`}
        />
      )}
    </div>
  );
};
